import {
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeSlashIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useContext, useMemo, useState } from "react";

import UserContext from "context/UserContext";

import Container from "components/atoms/Container";
import Spinner from "components/atoms/Spinner";
import NavHeader from "components/organisms/NavHeader";

import {
  classNames,
  error as toastError,
  success as toastSuccess,
} from "utils";

// The changelog is served through the products API (which proxies the
// env-appropriate CDN bucket server-side) to avoid browser CORS issues.
const API_BASE = process.env.REACT_APP_API_BASE as string;
const CHANGELOG_INDEX_URL = `${API_BASE}/products/changelog/index`;
const changelogEntryUrl = (id: string) =>
  `${API_BASE}/products/changelog/entries/${encodeURIComponent(id)}`;
// Admin-only: the full entry (customer + internal items) lives under a separate
// path that the portal only requests for admin users. Non-admins fetch the
// customer-only object above.
const changelogInternalEntryUrl = (id: string) =>
  `${API_BASE}/products/changelog/internal/entries/${encodeURIComponent(id)}`;
// Admin edit/delete go through the authenticated accounts service (gated
// server-side by the same admin check as the Insights cross-customer view).
// axios is configured with baseURL = API_BASE and the auth header.
const changelogAdminEntryPath = (id: string) =>
  `/accounts/products/changelog/admin/entries/${encodeURIComponent(id)}`;
// Admins list the FULL index (drafts included) through the authenticated
// accounts endpoint; non-admins use the public proxy above, which strips drafts.
const CHANGELOG_ADMIN_INDEX_PATH = "/accounts/products/changelog/admin/index";

// A generated entry starts life as a draft; it's only live for customers once
// an admin publishes it. `undefined` (legacy entries) is treated as published.
const isDraftEntry = (i: { published?: boolean }): boolean =>
  i.published === false;

const PAGE_SIZE = 12;

type Audience = "customer" | "internal";
type AudienceFilter = "all" | Audience;

// Admin gate for INTERNAL release notes. This intentionally mirrors the
// "see all customers" gate on the Insights page (RouteInsights) so the same
// people who can view cross-customer data can view internal/technical notes.
// Keep the env var name identical to RouteInsights so the allowlist stays in
// sync; default to the same fallback pattern.
const RESTRICTED_ADMIN_PATTERNS = (
  process.env.REACT_APP_ROUTE_INSIGHTS_RESTRICTED_ADMINS || "ae.branton9*"
)
  .split(",")
  .map((value) => value.trim().toLowerCase())
  .filter((value) => value.length > 0);

const emailMatchesPattern = (email: string, pattern: string): boolean => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPattern = pattern.trim().toLowerCase();
  if (!normalizedEmail || !normalizedPattern) return false;
  if (normalizedPattern.endsWith("*")) {
    return normalizedEmail.startsWith(normalizedPattern.slice(0, -1));
  }
  return normalizedEmail === normalizedPattern;
};

const isInternalNotesAdmin = (email?: string | null): boolean =>
  RESTRICTED_ADMIN_PATTERNS.some((pattern) =>
    emailMatchesPattern(email || "", pattern)
  );

interface ChangelogIndexItem {
  id: string;
  title: string;
  summary: string;
  date: string;
  generatedAt?: string;
  environment?: string;
  tags?: string[];
  highlights?: string[];
  url?: string;
  // Which audiences have at least one item in this entry. Used to hide
  // internal-only entries from non-admin viewers. Missing/empty is treated
  // as customer-safe.
  audiences?: Audience[];
  // Publication state. Generated entries start as drafts (published === false)
  // and only surface to customers once an admin publishes them. Missing =>
  // published (legacy entries that predate the gate).
  published?: boolean;
  publishedAt?: string;
}

interface ChangelogIndex {
  updatedAt?: string;
  entries: ChangelogIndexItem[];
}

interface ChangelogItem {
  title: string;
  description: string;
  audience: Audience;
  whereToFind?: string;
  docsUrl?: string;
}

interface ChangelogCategory {
  type: string;
  items: ChangelogItem[];
}

interface ChangelogEntry extends ChangelogIndexItem {
  version?: string | null;
  categories: ChangelogCategory[];
}

interface AvailableModel {
  tag: string;
  name: string;
  provider: string;
  supports_multimodal: boolean;
  zero_data_retention: boolean;
  reliable: boolean;
}

const CATEGORY_STYLES: Record<string, string> = {
  added: "bg-green-100 text-green-800",
  changed: "bg-blue-100 text-blue-800",
  improved: "bg-indigo-100 text-indigo-800",
  fixed: "bg-amber-100 text-amber-800",
  performance: "bg-purple-100 text-purple-800",
  deprecated: "bg-orange-100 text-orange-800",
  removed: "bg-red-100 text-red-800",
  security: "bg-rose-100 text-rose-800",
};

const formatDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

const monthKey = (iso: string): string => (iso || "").slice(0, 7);

const monthLabel = (key: string): string => {
  const d = new Date(`${key}-01T00:00:00Z`);
  if (isNaN(d.getTime())) return key;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
};

const fetchChangelogIndex = async (): Promise<ChangelogIndex> => {
  const res = await fetch(CHANGELOG_INDEX_URL, { cache: "no-store" });
  if (res.status === 404) return { entries: [] };
  if (!res.ok) throw new Error(`Failed to load changelog (${res.status})`);
  const data = (await res.json()) as ChangelogIndex;
  return { ...data, entries: data.entries || [] };
};

// Admin variant: the full index (published + drafts) via the authenticated
// accounts endpoint.
const fetchAdminChangelogIndex = async (): Promise<ChangelogIndex> => {
  const res = await axios.get<ChangelogIndex>(CHANGELOG_ADMIN_INDEX_PATH);
  const data = res.data || { entries: [] };
  return { ...data, entries: data.entries || [] };
};

const fetchChangelogEntry = async (
  id: string,
  admin: boolean
): Promise<ChangelogEntry> => {
  const url = admin ? changelogInternalEntryUrl(id) : changelogEntryUrl(id);
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load entry (${res.status})`);
  return res.json();
};

const fetchAvailableModels = async (): Promise<AvailableModel[]> => {
  const res = await fetch(`${API_BASE}/products/models/available`);
  if (!res.ok) throw new Error(`Failed to load models (${res.status})`);
  return res.json();
};

const AUDIENCE_OPTIONS: Audience[] = ["customer", "internal"];

const inputClass =
  "w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-passio-indigo focus:ring-passio-indigo";

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </label>
);

// Admin inline editor: edit any note, delete notes, edit entry-level fields, or
// delete the whole entry. Saving writes back through the authenticated accounts
// endpoint, which re-derives the public (customer-only) and internal objects.
const ChangelogEntryEditor = ({
  entry,
  onClose,
}: {
  entry: ChangelogEntry;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ChangelogEntry>(() =>
    JSON.parse(JSON.stringify(entry))
  );
  const [tagsInput, setTagsInput] = useState((entry.tags || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const updateItem = (
    catIdx: number,
    itemIdx: number,
    patch: Partial<ChangelogItem>
  ) => {
    setDraft((d) => {
      const next = { ...d, categories: [...d.categories] };
      const cat = { ...next.categories[catIdx] };
      const items = [...cat.items];
      items[itemIdx] = { ...items[itemIdx], ...patch };
      cat.items = items;
      next.categories[catIdx] = cat;
      return next;
    });
  };

  const deleteItem = (catIdx: number, itemIdx: number) => {
    setDraft((d) => {
      const next = { ...d, categories: [...d.categories] };
      const cat = { ...next.categories[catIdx] };
      cat.items = cat.items.filter((_, i) => i !== itemIdx);
      next.categories[catIdx] = cat;
      return next;
    });
  };

  const addItem = (catIdx: number) => {
    setDraft((d) => {
      const next = { ...d, categories: [...d.categories] };
      const cat = { ...next.categories[catIdx] };
      cat.items = [
        ...cat.items,
        { title: "", description: "", audience: "customer" as Audience },
      ];
      next.categories[catIdx] = cat;
      return next;
    });
  };

  const updateHighlight = (idx: number, value: string) => {
    setDraft((d) => {
      const highlights = [...(d.highlights || [])];
      highlights[idx] = value;
      return { ...d, highlights };
    });
  };

  const removeHighlight = (idx: number) => {
    setDraft((d) => ({
      ...d,
      highlights: (d.highlights || []).filter((_, i) => i !== idx),
    }));
  };

  const addHighlight = () => {
    setDraft((d) => ({ ...d, highlights: [...(d.highlights || []), ""] }));
  };

  const invalidate = () => {
    queryClient.invalidateQueries(["changelog-index"]);
    queryClient.invalidateQueries(["changelog-entry", entry.id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: ChangelogEntry = {
        ...draft,
        tags: tagsInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        highlights: (draft.highlights || [])
          .map((h) => h.trim())
          .filter(Boolean),
        categories: draft.categories
          .map((cat) => ({
            ...cat,
            items: cat.items.filter(
              (it) => it.title.trim() || it.description.trim()
            ),
          }))
          .filter((cat) => cat.items.length > 0),
      };
      await axios.put(changelogAdminEntryPath(entry.id), payload);
      toastSuccess("Changelog entry saved.");
      invalidate();
      onClose();
    } catch (e) {
      const ax = e as { response?: { data?: { cause?: string } } };
      toastError(
        ax?.response?.data?.cause || "Failed to save changelog entry."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (
      !window.confirm(
        "Delete this entire changelog entry? This removes it from the public feed and cannot be undone."
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(changelogAdminEntryPath(entry.id));
      toastSuccess("Changelog entry deleted.");
      invalidate();
      onClose();
    } catch (e) {
      const ax = e as { response?: { data?: { cause?: string } } };
      toastError(
        ax?.response?.data?.cause || "Failed to delete changelog entry."
      );
    } finally {
      setDeleting(false);
    }
  };

  const busy = saving || deleting;

  return (
    <div className="border-t border-gray-100 bg-gray-50 px-5 pb-5 pt-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-passio-indigo">
          Editing entry
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDeleteEntry}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            Delete entry
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-passio-indigo px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <FieldLabel>Title (public)</FieldLabel>
          <input
            className={inputClass}
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          />
        </div>
        <div>
          <FieldLabel>Summary (public)</FieldLabel>
          <textarea
            className={inputClass}
            rows={3}
            value={draft.summary}
            onChange={(e) =>
              setDraft((d) => ({ ...d, summary: e.target.value }))
            }
          />
        </div>

        <div>
          <FieldLabel>Highlights (public)</FieldLabel>
          <div className="space-y-2">
            {(draft.highlights || []).map((h, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className={inputClass}
                  value={h}
                  onChange={(e) => updateHighlight(i, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeHighlight(i)}
                  className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600"
                  aria-label="Remove highlight"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addHighlight}
              className="inline-flex items-center gap-1 text-xs font-medium text-passio-indigo hover:underline"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add highlight
            </button>
          </div>
        </div>

        <div>
          <FieldLabel>Tags (comma-separated)</FieldLabel>
          <input
            className={inputClass}
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
          />
        </div>

        {draft.categories.map((cat, catIdx) => (
          <div
            key={catIdx}
            className="rounded-md border border-gray-200 bg-white p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <input
                className="rounded border border-gray-200 px-2 py-1 text-xs font-semibold uppercase text-gray-600"
                value={cat.type}
                onChange={(e) =>
                  setDraft((d) => {
                    const next = { ...d, categories: [...d.categories] };
                    next.categories[catIdx] = {
                      ...next.categories[catIdx],
                      type: e.target.value,
                    };
                    return next;
                  })
                }
              />
              <button
                type="button"
                onClick={() => addItem(catIdx)}
                className="inline-flex items-center gap-1 text-xs font-medium text-passio-indigo hover:underline"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add note
              </button>
            </div>

            <div className="space-y-3">
              {cat.items.map((it, itemIdx) => (
                <div
                  key={itemIdx}
                  className={classNames(
                    "rounded-md border p-2.5",
                    it.audience === "internal"
                      ? "border-amber-200 bg-amber-50/60"
                      : "border-gray-200"
                  )}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <select
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                      value={
                        it.audience === "internal" ? "internal" : "customer"
                      }
                      onChange={(e) =>
                        updateItem(catIdx, itemIdx, {
                          audience: e.target.value as Audience,
                        })
                      }
                    >
                      {AUDIENCE_OPTIONS.map((a) => (
                        <option key={a} value={a}>
                          {a === "internal"
                            ? "Internal (admin only)"
                            : "Customer-facing"}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => deleteItem(catIdx, itemIdx)}
                      className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                      Delete note
                    </button>
                  </div>
                  <input
                    className={classNames(inputClass, "mb-2")}
                    placeholder="Note title"
                    value={it.title}
                    onChange={(e) =>
                      updateItem(catIdx, itemIdx, { title: e.target.value })
                    }
                  />
                  <textarea
                    className={classNames(inputClass, "mb-2")}
                    rows={3}
                    placeholder="Description"
                    value={it.description}
                    onChange={(e) =>
                      updateItem(catIdx, itemIdx, {
                        description: e.target.value,
                      })
                    }
                  />
                  <input
                    className={classNames(inputClass, "mb-2")}
                    placeholder="Where to find (optional)"
                    value={it.whereToFind || ""}
                    onChange={(e) =>
                      updateItem(catIdx, itemIdx, {
                        whereToFind: e.target.value,
                      })
                    }
                  />
                  <input
                    className={inputClass}
                    placeholder="Docs URL (optional)"
                    value={it.docsUrl || ""}
                    onChange={(e) =>
                      updateItem(catIdx, itemIdx, { docsUrl: e.target.value })
                    }
                  />
                </div>
              ))}
              {cat.items.length === 0 && (
                <p className="text-xs text-gray-400">
                  No notes in this category — it will be removed on save.
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Tag = ({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
}) => {
  if (!onClick) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
        {children}
      </span>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      }}
      className={classNames(
        "inline-flex cursor-pointer items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
        active
          ? "bg-passio-indigo text-white hover:opacity-90"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      )}
      title={active ? "Clear tag filter" : "Filter by this tag"}
    >
      {children}
    </span>
  );
};

const ChangelogEntryCard = ({
  item,
  audience,
  isAdmin,
  activeTag,
  onTagClick,
  defaultOpen,
}: {
  item: ChangelogIndexItem;
  audience: AudienceFilter;
  isAdmin: boolean;
  activeTag?: string | null;
  onTagClick?: (tag: string) => void;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [editing, setEditing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const queryClient = useQueryClient();
  const draft = isDraftEntry(item);

  const { data, isLoading, isError } = useQuery(
    ["changelog-entry", item.id, isAdmin ? "internal" : "public"],
    () => fetchChangelogEntry(item.id, isAdmin),
    { enabled: open, staleTime: 5 * 60 * 1000, retry: 1 }
  );

  const togglePublish = async () => {
    setPublishing(true);
    try {
      await axios.post(`${changelogAdminEntryPath(item.id)}/publish`, {
        published: draft,
      });
      toastSuccess(
        draft
          ? "Entry published — now live for customers."
          : "Entry unpublished — hidden from customers."
      );
      queryClient.invalidateQueries(["changelog-index"]);
      queryClient.invalidateQueries(["changelog-entry", item.id]);
    } catch (e) {
      const ax = e as { response?: { data?: { cause?: string } } };
      toastError(
        ax?.response?.data?.cause || "Failed to update publish state."
      );
    } finally {
      setPublishing(false);
    }
  };

  const visibleCategories = useMemo(() => {
    if (!data?.categories) return [];
    return data.categories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((it) => {
          const itemAudience: Audience =
            it.audience === "internal" ? "internal" : "customer";
          // Hard gate: non-admins never see internal items, regardless of the
          // requested audience filter.
          if (!isAdmin && itemAudience === "internal") return false;
          if (audience === "all") return true;
          return itemAudience === audience;
        }),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [data, audience, isAdmin]);

  return (
    <article
      id={`entry-${item.id}`}
      className="scroll-mt-24 rounded-lg border border-gray-200 bg-white shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-4 p-5 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-passio-indigo">
            <CalendarDaysIcon className="h-4 w-4" />
            {formatDate(item.date)}
          </div>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">
            {item.title}
          </h3>
          <p className="mt-1 text-sm text-gray-600">{item.summary}</p>
          {(item.tags?.length || 0) > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {item.tags?.map((t) => (
                <Tag
                  key={t}
                  active={
                    !!activeTag && activeTag.toLowerCase() === t.toLowerCase()
                  }
                  onClick={
                    onTagClick
                      ? (e) => {
                          // Don't toggle the card open/closed when filtering.
                          e.stopPropagation();
                          onTagClick(t);
                        }
                      : undefined
                  }
                >
                  {t}
                </Tag>
              ))}
            </div>
          )}
        </div>
        <span className="mt-1 flex-shrink-0 text-gray-400">
          {open ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </span>
      </button>

      {isAdmin && (
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-2">
          <span
            className={classNames(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
              draft
                ? "bg-amber-100 text-amber-800"
                : "bg-green-100 text-green-700"
            )}
          >
            {draft ? "Draft — not live" : "Published"}
          </span>
          <button
            type="button"
            onClick={togglePublish}
            disabled={publishing}
            className={classNames(
              "inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50",
              draft
                ? "bg-passio-indigo text-white hover:opacity-90"
                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
            )}
          >
            {draft ? (
              <>
                <CheckCircleIcon className="h-3.5 w-3.5" />
                {publishing ? "Publishing…" : "Publish"}
              </>
            ) : (
              <>
                <EyeSlashIcon className="h-3.5 w-3.5" />
                {publishing ? "Updating…" : "Unpublish"}
              </>
            )}
          </button>
        </div>
      )}

      {open && editing && data ? (
        <ChangelogEntryEditor entry={data} onClose={() => setEditing(false)} />
      ) : open ? (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner /> Loading details…
            </div>
          )}
          {isError && (
            <p className="text-sm text-red-600">
              Could not load the details for this entry.
            </p>
          )}
          {data && (
            <>
              {isAdmin && (
                <div className="mb-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </div>
              )}
              {(data.highlights?.length || 0) > 0 && (
                <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-gray-700">
                  {data.highlights?.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              )}
              {visibleCategories.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {isAdmin
                    ? "No changes match the selected audience filter."
                    : "No additional details for this update."}
                </p>
              ) : (
                <div className="space-y-4">
                  {visibleCategories.map((cat) => (
                    <div key={cat.type}>
                      <span
                        className={classNames(
                          "inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase",
                          CATEGORY_STYLES[cat.type.toLowerCase()] ||
                            "bg-gray-100 text-gray-700"
                        )}
                      >
                        {cat.type}
                      </span>
                      <ul className="mt-2 space-y-3">
                        {cat.items.map((it, i) => {
                          const internal = it.audience === "internal";
                          return (
                            <li
                              key={i}
                              className={classNames(
                                "text-sm",
                                internal
                                  ? "rounded-md border-l-2 border-amber-400 bg-amber-50/60 py-2 pl-3 pr-2"
                                  : ""
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {it.title}
                                </span>
                                {internal && (
                                  <span className="inline-flex items-center gap-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                    <LockClosedIcon className="h-2.5 w-2.5" />
                                    internal
                                  </span>
                                )}
                              </div>
                              <p className="whitespace-pre-line text-gray-600">
                                {it.description}
                              </p>
                              {it.whereToFind && (
                                <p className="mt-1 flex items-start gap-1.5 text-xs text-gray-500">
                                  <MapPinIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                  <span>
                                    <span className="font-medium text-gray-600">
                                      Where:
                                    </span>{" "}
                                    {it.whereToFind}
                                  </span>
                                </p>
                              )}
                              {it.docsUrl && (
                                <a
                                  href={it.docsUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-passio-indigo hover:underline"
                                >
                                  <BookOpenIcon className="h-3.5 w-3.5" />
                                  Learn more
                                </a>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
    </article>
  );
};

const ModelsSection = () => {
  const { data, isLoading, isError } = useQuery(
    ["available-models"],
    fetchAvailableModels,
    { staleTime: 10 * 60 * 1000, retry: 1 }
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Spinner /> Loading models…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <p className="py-8 text-sm text-red-600">
        Could not load the list of available models.
      </p>
    );
  }

  const models = [...data]
    .filter((m) => m.reliable)
    .sort(
      (a, b) =>
        a.provider.localeCompare(b.provider) || a.name.localeCompare(b.name)
    );

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3">Model</th>
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">API tag</th>
            <th className="px-4 py-3 text-center">Multimodal</th>
            <th className="px-4 py-3 text-center">ZDR eligible</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {models.map((m) => (
            <tr key={m.tag}>
              <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
              <td className="px-4 py-3 text-gray-600">{m.provider}</td>
              <td className="px-4 py-3">
                <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                  {m.tag}
                </code>
              </td>
              <td className="px-4 py-3 text-center">
                {m.supports_multimodal ? "Yes" : "—"}
              </td>
              <td className="px-4 py-3 text-center">
                {m.zero_data_retention ? "Yes" : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
        Showing generally-available models. ZDR-eligible models can be used with
        Zero Data Retention mode enabled.
      </p>
    </div>
  );
};

const Changelog = () => {
  const userContext = useContext(UserContext);
  const isAdmin = useMemo(
    () => isInternalNotesAdmin(userContext?.email),
    [userContext?.email]
  );

  const [tab, setTab] = useState<"changelog" | "models">("changelog");
  const [search, setSearch] = useState("");
  // Non-admins only ever see customer-facing notes; admins default to "all".
  const [audience, setAudience] = useState<AudienceFilter>("all");
  // Admin-only: filter by publication status (drafts are admin-only anyway).
  const [status, setStatus] = useState<"all" | "published" | "draft">("all");
  // Click a tag to filter to entries carrying it (click again to clear).
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(0);

  const handleTagClick = (tag: string) => {
    setActiveTag((cur) =>
      cur && cur.toLowerCase() === tag.toLowerCase() ? null : tag
    );
    setPage(0);
  };

  // Non-admins can never select "internal"; their effective view is locked to
  // customer-facing items regardless of the (hidden) filter state.
  const effectiveAudience: AudienceFilter = isAdmin ? audience : "customer";

  const { data, isLoading, isError, refetch } = useQuery(
    ["changelog-index", isAdmin ? "admin" : "public"],
    isAdmin ? fetchAdminChangelogIndex : fetchChangelogIndex,
    { staleTime: 60 * 1000, retry: 1 }
  );

  const allEntries = useMemo(() => {
    const entries = [...(data?.entries || [])];
    entries.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return entries;
  }, [data]);

  const months = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    allEntries.forEach((e) => {
      const k = monthKey(e.date);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    });
    return out;
  }, [allEntries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allEntries.filter((e) => {
      // Publication status (admin only; non-admins never receive drafts).
      if (isAdmin && status !== "all") {
        const entryIsDraft = isDraftEntry(e);
        if (status === "draft" && !entryIsDraft) return false;
        if (status === "published" && entryIsDraft) return false;
      }

      // Audience visibility. `audiences` is the set of audiences present in the
      // entry; if it's missing (legacy entries) we treat the entry as
      // customer-safe and rely on per-item filtering inside the card.
      const auds = e.audiences || [];
      if (!isAdmin) {
        // Hide entries that are explicitly internal-only.
        if (auds.length > 0 && !auds.includes("customer")) return false;
      } else if (effectiveAudience !== "all" && auds.length > 0) {
        if (!auds.includes(effectiveAudience)) return false;
      }

      // Tag filter (exact, case-insensitive) when a tag pill is active.
      if (activeTag) {
        const hasTag = (e.tags || []).some(
          (t) => t.toLowerCase() === activeTag.toLowerCase()
        );
        if (!hasTag) return false;
      }

      if (!q) return true;
      const haystack = [
        e.title,
        e.summary,
        ...(e.tags || []),
        ...(e.highlights || []),
      ]
        .join(" ")
        .toLowerCase();
      // Match every whitespace-separated term (AND), so "zdr openai" matches an
      // entry that mentions both words even if they aren't adjacent.
      const terms = q.split(/\s+/).filter(Boolean);
      return terms.every((term) => haystack.includes(term));
    });
  }, [allEntries, search, isAdmin, effectiveAudience, status, activeTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageEntries = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  const jumpToMonth = (key: string) => {
    setMonth(key);
    if (!key) return;
    const idx = filtered.findIndex((e) => monthKey(e.date) === key);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / PAGE_SIZE);
    setPage(targetPage);
    // Scroll after the page renders.
    setTimeout(() => {
      const el = document.getElementById(`entry-${filtered[idx].id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <NavHeader>
      <Container>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 xl:text-3xl">
            What&apos;s New
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Product updates, new models, and improvements to the Passio
            Nutrition AI Platform.
          </p>
        </div>

        <div className="mb-6 flex gap-6 border-b border-gray-200">
          {[
            { id: "changelog" as const, label: "Changelog" },
            { id: "models" as const, label: "Models & System" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={classNames(
                "border-b-2 px-1 pb-3 text-sm font-medium",
                tab === t.id
                  ? "border-passio-indigo text-passio-indigo"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "models" ? (
          <ModelsSection />
        ) : (
          <>
            {isAdmin && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <span className="font-semibold">Admin view.</span> You can see
                  internal/technical notes (marked{" "}
                  <span className="font-semibold">internal</span>) and{" "}
                  <span className="font-semibold">drafts</span>. New entries are
                  generated as drafts and stay hidden from customers until you{" "}
                  <span className="font-semibold">Publish</span> them. Regular
                  users only ever see published, customer-facing notes.
                </span>
              </div>
            )}

            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Search updates…"
                  className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
                />
              </div>

              {isAdmin && (
                <select
                  value={audience}
                  onChange={(e) =>
                    setAudience(e.target.value as AudienceFilter)
                  }
                  className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
                >
                  <option value="all">All changes</option>
                  <option value="customer">Customer-facing</option>
                  <option value="internal">Internal only</option>
                </select>
              )}

              {isAdmin && (
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as "all" | "published" | "draft");
                    setPage(0);
                  }}
                  className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
                >
                  <option value="all">All statuses</option>
                  <option value="published">Published</option>
                  <option value="draft">Drafts</option>
                </select>
              )}

              <select
                value={month}
                onChange={(e) => jumpToMonth(e.target.value)}
                className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
              >
                <option value="">Jump to date…</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
            </div>

            {activeTag && (
              <div className="mb-4 flex items-center gap-2 text-sm">
                <span className="text-gray-500">Filtered by tag:</span>
                <button
                  type="button"
                  onClick={() => setActiveTag(null)}
                  className="inline-flex items-center gap-1 rounded-full bg-passio-indigo px-2.5 py-0.5 text-xs font-medium text-white hover:opacity-90"
                >
                  {activeTag}
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {isLoading && (
              <div className="flex items-center gap-2 py-12 text-sm text-gray-500">
                <Spinner /> Loading changelog…
              </div>
            )}

            {isError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                We couldn&apos;t load the changelog right now.{" "}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => refetch()}
                >
                  Try again
                </button>
                .
              </div>
            )}

            {!isLoading && !isError && filtered.length === 0 && (
              <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                {allEntries.length === 0
                  ? "No changelog entries have been published yet. Check back soon!"
                  : "No updates match your search."}
              </div>
            )}

            <div className="space-y-4">
              {pageEntries.map((item, i) => (
                <ChangelogEntryCard
                  key={item.id}
                  item={item}
                  audience={effectiveAudience}
                  isAdmin={isAdmin}
                  activeTag={activeTag}
                  onTagClick={handleTagClick}
                  defaultOpen={safePage === 0 && i === 0}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <button
                  type="button"
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {safePage + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={safePage >= totalPages - 1}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}

            {data?.entries && data.entries.length > 0 && (
              <div className="mt-8 text-center">
                <a
                  href={CHANGELOG_INDEX_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                >
                  Raw changelog feed
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </>
        )}
      </Container>
    </NavHeader>
  );
};

export default Changelog;
